import { useIntl } from 'react-intl'

import {
  E2E_RENTAL_DETAILS_TAGS,
  E2E_RENTAL_DETAILS_TAGS_ADD,
  E2E_RENTAL_DETAILS_TAGS_REMOVE,
} from 'Constants/e2e'

import { AddTagButton } from './AddTagButton'
import { EditorDropdown } from './EditorDropdown'
import { TagBadge } from './TagBadge'
import { TagEditor } from './TagEditor'
import type { Tag } from './tagging.types'
import { useTagging } from './useTagging'

interface RentalTagsInnerProps {
  tags: Tag[]
  rentalId: string
  readOnly?: boolean
}

export const RentalTagsInner = (props: RentalTagsInnerProps) => {
  const { readOnly = false, tags, rentalId } = props
  const intl = useIntl()
  const tagging = useTagging()
  const isEditMode = !readOnly && tagging.mode === 'edit'
  const isDisabled = tagging.isAddingTag || tagging.isRemovingTag
  const hasTags = tags.length > 0
  const showAddButton = !readOnly && !isEditMode
  const showEmptyState = !hasTags && !isEditMode

  const handleAddClick = () => {
    if (readOnly) {
      return
    }

    tagging.enterEditMode(rentalId)
  }

  const handleTagNameClick = () => {
    if (isDisabled || readOnly) {
      return
    }

    tagging.enterEditMode(rentalId)
  }

  const handleRemoveTag = (tag: Tag) => {
    if (readOnly) {
      return
    }

    tagging.removeTag([rentalId], tag)
  }

  return (
    <div className="RentalTagsRow" data-e2e={E2E_RENTAL_DETAILS_TAGS}>
      <span className="RentalTagsRow-label pe-2">
        {intl.formatMessage({ id: 'Tags' })}:
      </span>

      <div className="RentalTagsRow-tags">
        {tags.map(tag => (
          <TagBadge
            key={tag.id}
            color={tag.color}
            disableRemove={isDisabled}
            name={tag.name}
            onNameClick={
              isEditMode || readOnly ? undefined : handleTagNameClick
            }
            onRemove={() => handleRemoveTag(tag)}
            removeDataE2e={E2E_RENTAL_DETAILS_TAGS_REMOVE}
            showRemove={!readOnly}
          />
        ))}

        {isEditMode && (
          <div className="TagEditor-anchor">
            <TagEditor
              ariaLabel={intl.formatMessage({ id: 'Tags_add_tag_input' })}
              disabled={isDisabled}
              onExit={tagging.exitEditMode}
            />
            <EditorDropdown placement="start" />
          </div>
        )}

        {showAddButton && (
          <span data-e2e={E2E_RENTAL_DETAILS_TAGS_ADD}>
            <AddTagButton onClick={handleAddClick} visible />
          </span>
        )}

        {showEmptyState && (
          <span className="RentalTagsRow-empty">
            {intl.formatMessage({ id: 'Tags_no_tags' })}
          </span>
        )}
      </div>
    </div>
  )
}
